import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X, Check, FileText } from 'lucide-react';
import { Dropdown } from './ui/dropdown-01';
import Lottie from 'lottie-react';
import uploadAnimation from '../assets/Upload Files.json';

const MOBILE_PREVIEW_SCALE = 0.68;
const MOBILE_PREVIEW_MIN_SCALE = 0.3;
const MOBILE_SCREEN_WIDTH = 450;
const MOBILE_SCREEN_HEIGHT = 974;
const MOBILE_FRAME_PADDING = 16;
const MOBILE_FRAME_WIDTH = MOBILE_SCREEN_WIDTH + MOBILE_FRAME_PADDING * 2;
const MOBILE_FRAME_HEIGHT = MOBILE_SCREEN_HEIGHT + MOBILE_FRAME_PADDING * 2;
// Modal başlığı, taslak alanı, aksiyon butonları ve iç boşluklar için ayrılan
// dikey pay — kalan alan telefon önizlemesine göre ölçeklenir.
const MOBILE_PREVIEW_VERTICAL_RESERVE = 220;

const EditCampaignModal = ({
  show,
  campaign,
  formData,
  setFormData,
  emptyFields,
  setEmptyFields,
  isDragging,
  setIsDragging,
  onImageUpload,
  onRemoveImage,
  onInputChange,
  onUpdate,
  onPublish,
  onUnpublish,
  onCancel,
  hasChanges,
  canPublish = true,
  updateLoading,
  publishLoading,
  unpublishLoading,
  onApplyTemplate,
  templates = [],
  type = 'personal',
  activeSubTab = 'personal'
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [previewScale, setPreviewScale] = useState(MOBILE_PREVIEW_SCALE);

  useEffect(() => {
    setSelectedTemplateId('');
  }, [campaign?.id]);

  // Telefon önizlemesini kullanılabilir dikey alana göre ölçekle — pencere
  // dikeyde küçülünce simülasyon da küçülsün, taşıp diğer öğelerin üstüne binmesin.
  useEffect(() => {
    if (!show) return undefined;
    const computeScale = () => {
      const available = window.innerHeight * 0.88 - MOBILE_PREVIEW_VERTICAL_RESERVE;
      const next = Math.max(
        MOBILE_PREVIEW_MIN_SCALE,
        Math.min(MOBILE_PREVIEW_SCALE, available / MOBILE_FRAME_HEIGHT)
      );
      setPreviewScale(next);
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, [show]);

  // Modal açıkken arka plan (body) scroll'unu kilitle — sağ scrollbar şeridi ve
  // arka plan kayması nedeniyle oluşan beyaz alanları engeller.
  useEffect(() => {
    if (!show) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  if (!show || !campaign) return null;

  const handleImageUpload = (file, imageField = 'campaignImage') => {
    if (file) {
      onImageUpload(file, imageField);
      if (emptyFields.includes(imageField)) {
        setEmptyFields(emptyFields.filter(field => field !== imageField));
      }
    }
  };

  const handleInputChange = (e) => {
    onInputChange(e);
    const { name } = e.target;
    if (emptyFields.includes(name)) {
      setEmptyFields(emptyFields.filter(field => field !== name));
    }
  };

  // Mobil önizleme için veri hazırla
  const getPreviewData = () => {
    // Features'ı array'e çevir
    const features = formData.campaignFeatures
      ? formData.campaignFeatures.split('\n').filter(f => f.trim())
      : [];
    const terms = (formData.campaignTermsAndConditions || campaign?.campaignTermsAndConditions || '')
      .split(/\n|#/)
      .map((item) => item.trim())
      .filter(Boolean);
    
    // Tarih bilgisini hazırla
    let validUntil = null;
    if (formData.hideShowEndDate !== 1 && formData.overwriteEndDate) {
      validUntil = formData.overwriteEndDate;
    } else if (formData.hideShowEndDate !== 1 && campaign?.endDate) {
      validUntil = campaign.endDate;
    }

    return {
      image: formData.campaignImage || campaign?.image || campaign?.campaignImage,
      title: formData.campaignTitle || campaign?.title || '',
      description: formData.campaignContent || campaign?.campaignContent || campaign?.description || '',
      detailDescription: formData.campaignContent || campaign?.campaignContent || campaign?.description || '',
      features,
      terms,
      validUntil,
      hideShowEndDate: formData.hideShowEndDate ?? campaign?.hideShowEndDate ?? 0
    };
  };

  const previewData = getPreviewData();
  const previewWidth = MOBILE_FRAME_WIDTH * previewScale;
  const previewHeight = MOBILE_FRAME_HEIGHT * previewScale;

  const handleTemplateSelect = (id) => {
    setSelectedTemplateId(id);
    if (id) {
      const template = templates.find((t) => String(t.id) === String(id));
      if (template && onApplyTemplate) {
        onApplyTemplate(template);
      }
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 overflow-y-auto">
      <div className="bg-white rounded-xl p-4 w-full max-w-6xl max-h-[88vh] flex flex-col my-3">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex-shrink-0">Kampanya Düzenle</h2>

        {/* Taslaktan Doldur */}
        {templates.length > 0 && onApplyTemplate && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-2">
              <FileText size={15} className="text-blue-600" />
              Taslaktan Doldur
            </label>
            <Dropdown
              items={[
                { value: '', label: 'Taslak seçin...' },
                ...templates.map((t) => ({
                  value: String(t.id),
                  label: t.campaignTitle || t.templateName || `Taslak #${t.id}`,
                })),
              ]}
              value={String(selectedTemplateId || '')}
              onChange={handleTemplateSelect}
              placeholder="Taslak seçin..."
              simple
              className="max-w-md"
            />
          </div>
        )}

        {/* İki Kolonlu Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
          {/* Sol Kolon: Form */}
          <div className="space-y-3 overflow-y-auto pr-2 min-h-0">
          {/* Kampanya Başlığı */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Kampanya Başlığı</label>
            <input
              type="text"
              name="campaignTitle"
              value={formData.campaignTitle}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                emptyFields.includes('campaignTitle')
                  ? 'border-red-500 focus:ring-red-500 animate-pulse'
                  : 'border-gray-200 focus:ring-blue-500'
              }`}
              placeholder="Kampanya başlığı"
            />
          </div>

          {/* Sıralama Önceliği */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Sıralama Önceliği</label>
            <input
              type="number"
              min="1"
              step="1"
              name="priority"
              value={formData.priority ?? ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 border-gray-200 focus:ring-blue-500"
              placeholder="Orn: 1 (en yuksek oncelik)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Kucuk sayi daha yuksek oncelik demektir. Ornek: 1 en ustte gosterilir.
            </p>
          </div>

          {/* Kampanya Kısa İçerik */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Kampanya Kısa İçerik</label>
            <textarea
              rows="2"
              maxLength={26}
              name="campaignContentShort"
              value={formData.campaignContentShort}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                emptyFields.includes('campaignContentShort')
                  ? 'border-red-500 focus:ring-red-500 animate-pulse'
                  : 'border-gray-200 focus:ring-blue-500'
              }`}
              placeholder="Kampanya kısa açıklaması (maks. 26 karakter)"
            />
            <p className={`text-xs mt-1 text-right ${(formData.campaignContentShort?.length || 0) >= 26 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
              {formData.campaignContentShort?.length || 0}/26
            </p>
          </div>

          {/* Kampanya İçeriği */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Kampanya İçeriği</label>
            <textarea
              rows="5"
              name="campaignContent"
              value={formData.campaignContent}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                emptyFields.includes('campaignContent')
                  ? 'border-red-500 focus:ring-red-500 animate-pulse'
                  : 'border-gray-200 focus:ring-blue-500'
              }`}
              placeholder="Kampanya içeriğini detaylı olarak açıklayın"
            />
          </div>

          {/* Kampanya Özellikleri */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Kampanya Özellikleri</label>
            <textarea
              rows="3"
              name="campaignFeatures"
              value={formData.campaignFeatures}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                emptyFields.includes('campaignFeatures')
                  ? 'border-red-500 focus:ring-red-500 animate-pulse'
                  : 'border-gray-200 focus:ring-blue-500'
              }`}
              placeholder="Her satıra bir özellik yazın&#10;- Tüm kullanıcılar için geçerli&#10;- Minimum 100 TL alışveriş&#10;- Belirli kategorilerde geçerli"
            />
          </div>

          {/* Kampanya Şart ve Koşulları */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Kampanya Şart ve Koşulları</label>
            <textarea
              rows="6"
              name="campaignTermsAndConditions"
              value={formData.campaignTermsAndConditions}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 font-mono text-sm ${
                emptyFields.includes('campaignTermsAndConditions')
                  ? 'border-red-500 focus:ring-red-500 animate-pulse'
                  : 'border-gray-200 focus:ring-blue-500'
              }`}
              placeholder="Kampanya şart ve koşullarını detaylı olarak yazın"
            />
          </div>

          {/* Tarih Override ve Bitiş Gizleme (Genel ve Kişisel Kampanyalar) */}
          {(type === 'general' || (type === 'personal' && activeSubTab === 'personal')) && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="text"
                    name="overwriteStartDate"
                    value={formData.overwriteStartDate}
                    onChange={onInputChange}
                    className="w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 border-gray-200 focus:ring-blue-500"
                    placeholder="2026/01/01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="text"
                    name="overwriteEndDate"
                    value={formData.overwriteEndDate}
                    onChange={onInputChange}
                    className="w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 border-gray-200 focus:ring-blue-500"
                    placeholder="2026/12/31"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 pl-1">
                <input
                  id="hideShowEndDate"
                  name="hideShowEndDate"
                  type="checkbox"
                  checked={!!formData.hideShowEndDate}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, hideShowEndDate: e.target.checked ? 1 : 0 }))
                  }
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="hideShowEndDate" className="text-xs font-medium text-gray-700">
                  Uygulamadaki geçerlilik tarihi kısmını gizle
                </label>
              </div>
            </div>
          )}

          {/* Bitiş Gizleme (Referans Çekleri ve Hediye Çekleri) */}
          {(type === 'personal' && (activeSubTab === 'referral' || activeSubTab === 'gift')) && (
            <div className="flex items-center gap-2 pt-2 pl-1">
              <input
                id="hideShowEndDate"
                name="hideShowEndDate"
                type="checkbox"
                checked={!!formData.hideShowEndDate}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, hideShowEndDate: e.target.checked ? 1 : 0 }))
                }
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="hideShowEndDate" className="text-xs font-medium text-gray-700">
              Uygulamadaki geçerlilik tarihi kısmını gizle
              </label>
            </div>
          )}

          {/* Kampanya Görseli */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Mobil App Görseli</label>
            <div
              className={`border-2 border-dashed rounded-lg p-3 transition-all duration-300 ${
                emptyFields.includes('campaignImage')
                  ? 'border-red-500 bg-red-50 animate-pulse'
                  : isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleImageUpload(files[0]);
                }
              }}
            >
              <div className="flex flex-col items-center gap-2">
                {formData.campaignImage && formData.campaignImage.trim() !== '' ? (
                  <div className="relative w-full max-w-xs mb-1">
                    <img
                      src={formData.campaignImage}
                      alt=""
                      className="w-full h-auto object-cover rounded-lg"
                      style={{ aspectRatio: '14/9' }}
                    />
                    <button
                      onClick={() => {
                        onRemoveImage('campaignImage');
                      }}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                      title="Görseli kaldır"
                    >
                      <X className="text-xl" />
                    </button>
                  </div>
                ) : (
                  <Lottie
                    animationData={uploadAnimation}
                    loop={true}
                    style={{ width: '120px', height: '120px' }}
                  />
                )}

                <div className="text-center space-y-2">
                  <p className="text-xs text-gray-600">
                    Görseli buraya sürükleyin veya
                  </p>
                  <label htmlFor="campaign-image-edit" className="cursor-pointer">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium">
                      <Upload className="text-base" />
                      <span>Dosya Seçin</span>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      id="campaign-image-edit"
                      accept="image/png,image/webp"
                      onChange={(e) => {
                        if (e.target.files.length > 0) {
                          handleImageUpload(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                  <p className="text-xs text-gray-500">
                    Önerilen: 1120 x 720 px (14:9) · PNG/WEBP · max. 500 KB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Kiosk Görseli (Genel ve Kişisel Kampanyalar için) */}
          {(type === 'general' || activeSubTab === 'personal') && (() => {
            // Kampanya tipine göre boyutları belirle
            const isGeneral = type === 'general';
            const imageWidth = isGeneral ? 260 : 320;
            const imageHeight = isGeneral ? 230 : 441;
            const aspectRatio = `${imageWidth}/${imageHeight}`;
            const recommendedText = `Önerilen: ${imageWidth} x ${imageHeight} px · PNG/WEBP · max. 500 KB`;
            
            return (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Kiosk Küçük Banner Görseli</label>
                <div
                  className={`border-2 border-dashed rounded-lg p-3 transition-all duration-300 ${
                    emptyFields.includes('campaignImage2')
                      ? 'border-red-500 bg-red-50 animate-pulse'
                      : isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      handleImageUpload(files[0], 'campaignImage2');
                    }
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    {formData.campaignImage2 && formData.campaignImage2.trim() !== '' ? (
                      <div className="relative w-full max-w-xs mb-1">
                        <img
                          src={formData.campaignImage2}
                          alt="Kiosk Görseli"
                          className="w-full h-auto object-cover rounded-lg"
                          style={{ aspectRatio }}
                        />
                        <button
                          onClick={() => {
                            onRemoveImage('campaignImage2');
                          }}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                          title="Görseli kaldır"
                        >
                          <X className="text-xl" />
                        </button>
                      </div>
                    ) : (
                      <Lottie
                        animationData={uploadAnimation}
                        loop={true}
                        style={{ width: '120px', height: '120px' }}
                      />
                    )}

                    <div className="text-center space-y-2">
                      <p className="text-xs text-gray-600">
                        Görseli buraya sürükleyin veya
                      </p>
                      <label htmlFor="campaign-image2-edit" className="cursor-pointer">
                        <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium">
                          <Upload className="text-base" />
                          <span>Dosya Seçin</span>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          id="campaign-image2-edit"
                          accept="image/png,image/webp"
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              handleImageUpload(e.target.files[0], 'campaignImage2');
                            }
                          }}
                        />
                      </label>
                      <p className="text-xs text-gray-500">
                        {recommendedText}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Kiosk Büyük Banner Görseli (Sadece Genel Kampanyalar için) */}
          {type === 'general' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Kiosk Büyük Banner Görseli</label>
              <div
                className={`border-2 border-dashed rounded-lg p-3 transition-all duration-300 ${
                  emptyFields.includes('campaignImage3')
                    ? 'border-red-500 bg-red-50 animate-pulse'
                    : isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    handleImageUpload(files[0], 'campaignImage3');
                  }
                }}
              >
                <div className="flex flex-col items-center gap-2">
                  {formData.campaignImage3 && formData.campaignImage3.trim() !== '' ? (
                    <div className="relative w-full max-w-xs mb-1">
                      <img
                        src={formData.campaignImage3}
                        alt="Kiosk Büyük Banner Görseli"
                        className="w-full h-auto object-cover rounded-lg"
                        style={{ aspectRatio: '1000/392' }}
                      />
                      <button
                        onClick={() => {
                          onRemoveImage('campaignImage3');
                        }}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                        title="Görseli kaldır"
                      >
                        <X className="text-xl" />
                      </button>
                    </div>
                  ) : (
                    <Lottie
                      animationData={uploadAnimation}
                      loop={true}
                      style={{ width: '120px', height: '120px' }}
                    />
                  )}

                  <div className="text-center space-y-2">
                    <p className="text-xs text-gray-600">
                      Görseli buraya sürükleyin veya
                    </p>
                    <label htmlFor="campaign-image3-edit" className="cursor-pointer">
                      <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium">
                        <Upload className="text-base" />
                        <span>Dosya Seçin</span>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        id="campaign-image3-edit"
                        accept="image/png,image/webp"
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            handleImageUpload(e.target.files[0], 'campaignImage3');
                          }
                        }}
                      />
                    </label>
                    <p className="text-xs text-gray-500">
                      Önerilen: 1000 x 392 px · PNG/WEBP · max. 500 KB
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>

          {/* Sağ Kolon: Telefon Simülasyonu */}
          <div className="hidden lg:flex flex-col items-center overflow-y-auto min-h-0 pt-1">
            <p className="text-xs font-medium text-gray-500 mb-2 self-center flex-shrink-0">Mobil Önizleme</p>
            <div
              className="relative shrink-0 overflow-hidden"
              style={{ width: previewWidth, height: previewHeight }}
            >
              <div
                className="absolute top-0 left-0 origin-top-left"
                style={{ transform: `scale(${previewScale})` }}
              >
              {/* Telefon Frame */}
              <div className="relative bg-gray-900 rounded-[3.6rem] p-4 shadow-2xl">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-38 h-7 bg-gray-900 rounded-b-2xl z-10" style={{ width: '38.4px', height: '7.2px' }}></div>
                
                {/* Ekran */}
                <div className="bg-white rounded-[3rem] overflow-hidden" style={{ width: '450px', height: '974px' }}>
                  {/* Status Bar */}
                  <div className="bg-white h-14 flex items-center justify-between px-7 pt-2.5" style={{ height: '56px' }}>
                    <span className="text-sm font-semibold text-gray-900">9:41</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-2.5 border border-gray-900 rounded-sm" style={{ width: '20px', height: '10px' }}></div>
                      <div className="w-6 h-2.5 bg-gray-900 rounded-sm" style={{ width: '24px', height: '10px' }}></div>
                    </div>
                  </div>

                  {/* Mobil Kampanya Detay İçeriği */}
                  <div className="overflow-y-auto bg-white" style={{ height: '912px' }}>
                    {/* Drag Handle */}
                    <div className="flex justify-center py-3.5 bg-white" style={{ paddingTop: '14px', paddingBottom: '14px' }}>
                      <div className="w-14 h-1.5 bg-gray-300 rounded-full" style={{ width: '56px', height: '6px' }}></div>
                    </div>

                    {/* Campaign Image */}
                    {previewData.image ? (
                      <div className="relative w-full overflow-hidden" style={{ height: '289px' }}>
                        <img
                          src={previewData.image}
                          alt={previewData.title}
                          className="w-full h-full object-cover"
                          style={{ maxWidth: '100%' }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent" style={{ height: '96px' }}></div>
                      </div>
                    ) : (
                      <div className="relative w-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center" style={{ height: '289px' }}>
                        <div className="text-center text-gray-400">
                          <Upload className="w-14 h-14 mx-auto mb-2.5 opacity-50" style={{ width: '56px', height: '56px' }} />
                          <p className="text-base font-medium">Görsel yok</p>
                        </div>
                      </div>
                    )}

                    {/* Campaign Info */}
                    <div className="px-6 pt-6 pb-10" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '30px', paddingBottom: '40px' }}>
                      {/* Title */}
                      {previewData.title && (
                        <h2 className="font-bold text-gray-900 mb-2.5 leading-tight" style={{ fontSize: '26px' }}>
                          {previewData.title}
                        </h2>
                      )}

                      {/* Description */}
                      {previewData.description && (
                        <p className="text-gray-600 mb-5" style={{ fontSize: '18px', lineHeight: '26px' }}>
                          {previewData.description}
                        </p>
                      )}

                      {/* Date - HideShowEndDate === 1 ise gösterilmez */}
                      {previewData.hideShowEndDate !== 1 && previewData.validUntil && (
                        <div className="bg-orange-50 px-3.5 py-3 rounded-lg mb-7 border-l-4 border-orange-500 self-start" style={{ paddingLeft: '14px', paddingRight: '14px', paddingTop: '12px', paddingBottom: '12px' }}>
                          <p className="font-semibold text-orange-800" style={{ fontSize: '15.5px' }}>
                            Geçerlilik: {previewData.validUntil}
                          </p>
                        </div>
                      )}

                      {/* Features */}
                      {previewData.features.length > 0 && (
                        <div className="mb-7">
                          <h3 className="font-semibold text-gray-900 mb-3.5" style={{ fontSize: '20px' }}>
                            Kampanya Özellikleri
                          </h3>
                          <div className="space-y-3" style={{ gap: '12px' }}>
                            {previewData.features.map((feature, index) => (
                              <div key={index} className="flex items-start gap-3.5" style={{ gap: '14px' }}>
                                <div className="mt-0.5 flex-shrink-0" style={{ marginTop: '2px' }}>
                                  <Check size={19} color="#FF4D00" strokeWidth={2.5} />
                                </div>
                                <p className="text-gray-700 leading-6 flex-1" style={{ fontSize: '16.5px', lineHeight: '24px' }}>
                                  {feature.trim()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Terms */}
                      {previewData.terms.length > 0 && (
                        <div className="pt-5 border-t border-gray-200" style={{ paddingTop: '20px' }}>
                          <h3 className="font-semibold text-gray-600 mb-2.5" style={{ fontSize: '18px' }}>
                            Şartlar ve Koşullar
                          </h3>
                          <div className="space-y-2">
                            {previewData.terms.map((term, index) => (
                              <div key={index} className="flex items-start gap-2.5">
                                <span
                                  className="block rounded-full bg-gray-400 flex-shrink-0"
                                  style={{ width: '6px', height: '6px', marginTop: '8px' }}
                                />
                                <p className="text-gray-400 flex-1" style={{ fontSize: '14.5px', lineHeight: '22px' }}>
                                  {term}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="relative z-10 flex gap-2 mt-4 pt-4 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            İptal
          </button>
          <>
            <button
              onClick={onUpdate}
              disabled={updateLoading || publishLoading || unpublishLoading || !hasChanges}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateLoading ? '⏳ Değişiklikler kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </button>
            {campaign.isActiveApp === 1 || campaign.status === 'active' ? (
              <button
                onClick={onUnpublish}
                disabled={updateLoading || publishLoading || unpublishLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {unpublishLoading ? '⏳ Yayından kaldırılıyor...' : 'Yayından Kaldır'}
              </button>
            ) : (
              <button
                onClick={onPublish}
                disabled={updateLoading || publishLoading || unpublishLoading || !canPublish}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {publishLoading ? '⏳ Yayınlanıyor...' : 'Yayınla'}
              </button>
            )}
          </>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditCampaignModal;

